import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Radio, ChevronDown, ChevronRight, ArrowRight, Plus, BellRing, X } from "lucide-react";

const STATUS_META = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "In progress", cls: "bg-sky-100 text-sky-800" },
  ready: { label: "Ready", cls: "bg-emerald-100 text-emerald-800" },
  complete: { label: "Complete", cls: "bg-gray-200 text-gray-600" },
};
const NEXT_STATUS = { open: "in_progress", in_progress: "ready", ready: "complete" };

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-CA", {
      timeZone: "America/Edmonton", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function RequestForm({ onCreated, onClose, noOneOnDuty }) {
  const emptyPart = { part_name: "", part_number: "", quantity: "1" };
  const [parts, setParts] = useState([{ ...emptyPart }]);
  const [tail, setTail] = useState("");
  const [jc, setJc] = useState("");
  const [busy, setBusy] = useState(false);
  const setPart = (idx, k, v) => setParts((arr) => arr.map((p, i) => (i === idx ? { ...p, [k]: v } : p)));
  const addPart = () => setParts((arr) => [...arr, { ...emptyPart }]);
  const removePart = (idx) => setParts((arr) => arr.filter((_, i) => i !== idx));

  const submit = async () => {
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (!p.part_name.trim()) { toast.error(`Part ${i + 1}: part name is required.`); return; }
      if (!p.part_number.trim()) { toast.error(`Part ${i + 1}: part number is required.`); return; }
      const qty = parseInt(p.quantity, 10);
      if (!qty || qty < 1) { toast.error(`Part ${i + 1}: quantity must be at least 1.`); return; }
    }
    if (!tail.trim()) { toast.error("Aircraft Tail is required."); return; }
    if (!jc.trim()) { toast.error("JC Number is required."); return; }
    setBusy(true);
    try {
      await api.post("/parts/requests", {
        parts: parts.map((p) => ({
          part_name: p.part_name.trim(),
          part_number: p.part_number.trim(),
          quantity: parseInt(p.quantity, 10),
        })),
        aircraft_tail: tail.trim(),
        jc_number: jc.trim(),
      });
      toast.success("Parts request submitted");
      setParts([{ ...emptyPart }]);
      setTail("");
      setJc("");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-summit-hairline rounded-lg bg-white p-4" data-testid="parts-request-form">
      {noOneOnDuty && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="parts-no-duty-note">
          No one is on duty in Stores right now — requests can't be picked up until someone starts duty.
        </div>
      )}
      <div className="space-y-2">
        {parts.map((p, idx) => (
          <div key={idx} className="grid grid-cols-2 sm:grid-cols-[2fr_1fr_80px_32px] gap-3 items-end" data-testid={`parts-form-row-${idx}`}>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Part name *</Label>
              <Input className="mt-1 h-8 text-sm" value={p.part_name} onChange={(e) => setPart(idx, "part_name", e.target.value)} data-testid={`parts-form-name-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Part number *</Label>
              <Input className="mt-1 h-8 text-sm font-mono" value={p.part_number} onChange={(e) => setPart(idx, "part_number", e.target.value)} data-testid={`parts-form-number-${idx}`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Qty *</Label>
              <Input className="mt-1 h-8 text-sm font-mono" type="number" min="1" value={p.quantity} onChange={(e) => setPart(idx, "quantity", e.target.value)} data-testid={`parts-form-quantity-${idx}`} />
            </div>
            <div className="flex justify-end">
              {parts.length > 1 && (
                <button type="button" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50" title="Remove this part" onClick={() => removePart(idx)} data-testid={`parts-form-remove-${idx}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" className="h-7 font-mono text-[10px] uppercase tracking-widest" onClick={addPart} data-testid="parts-form-add-part">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add another part
        </Button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-gray-500">Aircraft Tail *</Label>
          <Input className="mt-1 h-8 text-sm font-mono" placeholder="e.g. C-FSAX" value={tail} onChange={(e) => setTail(e.target.value)} data-testid="parts-form-tail" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-gray-500">JC Number *</Label>
          <Input className="mt-1 h-8 text-sm font-mono" value={jc} onChange={(e) => setJc(e.target.value)} data-testid="parts-form-jc" />
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onClose} data-testid="parts-form-cancel">Cancel</Button>
        <Button size="sm" className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submit} disabled={busy || noOneOnDuty} data-testid="parts-form-submit">
          {busy ? "Submitting…" : `Submit request${parts.length > 1 ? ` · ${parts.length} parts` : ""}`}
        </Button>
      </div>
    </div>
  );
}

function RequestCard({ item, canManage, onStatusChange }) {
  const meta = STATUS_META[item.status] || STATUS_META.open;
  const next = NEXT_STATUS[item.status];
  const parts = item.parts && item.parts.length
    ? item.parts
    : [{ part_name: item.part_name, part_number: item.part_number, quantity: item.quantity }];
  return (
    <div className="border border-summit-hairline rounded-md bg-white px-3 py-2" data-testid={`parts-request-card-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm truncate">
          {parts[0].part_name}
          {parts.length > 1 && <span className="text-gray-500 font-normal"> +{parts.length - 1} more</span>}
        </span>
        <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${meta.cls}`} data-testid={`parts-status-badge-${item.id}`}>
          {meta.label}
        </span>
        {canManage && item.status !== "complete" && (
          <div className="ml-auto flex items-center gap-1.5">
            <Select value={item.status} onValueChange={(v) => onStatusChange(item, v)}>
              <SelectTrigger className="h-7 w-[120px] text-[11px]" data-testid={`parts-status-select-${item.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k} data-testid={`parts-status-option-${k}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {next && (
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onStatusChange(item, next)} data-testid={`parts-advance-btn-${item.id}`}>
                {STATUS_META[next].label} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="mt-1 space-y-0.5" data-testid={`parts-request-items-${item.id}`}>
        {parts.map((p, i) => (
          <div key={i} className="font-mono text-[11px] text-gray-600">
            {p.part_name} · P/N {p.part_number} · Qty {p.quantity}
          </div>
        ))}
      </div>
      <div className="mt-0.5 flex items-center gap-2 flex-wrap font-mono text-[10px] text-gray-400">
        <span>{item.requested_by_name} · {fmtWhen(item.created_at)}</span>
        {item.aircraft_tail && <span className="text-gray-500">· Tail: {item.aircraft_tail}</span>}
        {item.jc_number && <span className="text-gray-500">· JC: {item.jc_number}</span>}
      </div>
    </div>
  );
}

function OnDutyPanel({ staff, loaded }) {
  return (
    <div className="border border-summit-hairline rounded-lg bg-white p-5" data-testid="parts-on-duty-panel">
      <h2 className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
        <Radio className="w-4 h-4 text-emerald-600" /> Stores on duty
      </h2>
      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">Live</p>
      <div className="mt-4 space-y-2.5">
        {!loaded ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : staff.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="parts-on-duty-empty">No one is on duty right now.</p>
        ) : (
          staff.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2" data-testid={`parts-on-duty-row-${s.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0 bg-emerald-500 animate-pulse" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.display_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 truncate">
                    {s.job_title || "—"}{s.city ? ` · ${s.city}` : ""}
                  </div>
                </div>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest shrink-0 text-emerald-600">
                On duty{s.since ? ` · ${fmtWhen(s.since)}` : ""}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Looping two-tone alarm (Web Audio) — fires while any Open request exists for
// a Stores handler. Browsers gate audio behind a user gesture; a pointerdown
// listener resumes the context as soon as the user interacts.
function useOpenRequestAlarm(enabled) {
  const ctxRef = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return undefined;
    const ctx = ctxRef.current && ctxRef.current.state !== "closed" ? ctxRef.current : new AC();
    ctxRef.current = ctx;
    const beep = () => {
      if (ctx.state === "suspended") { ctx.resume().catch(() => {}); return; }
      const t = ctx.currentTime;
      [880, 660, 880].forEach((freq, idx) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "square";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, t + idx * 0.2);
        g.gain.exponentialRampToValueAtTime(0.14, t + idx * 0.2 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + idx * 0.2 + 0.18);
        o.connect(g); g.connect(ctx.destination);
        o.start(t + idx * 0.2); o.stop(t + idx * 0.2 + 0.2);
      });
    };
    const resume = () => { if (ctx.state === "suspended") ctx.resume().catch(() => {}); };
    window.addEventListener("pointerdown", resume);
    beep();
    const iv = setInterval(beep, 2500);
    return () => {
      clearInterval(iv);
      window.removeEventListener("pointerdown", resume);
      ctx.close().catch(() => {});
      ctxRef.current = null;
    };
  }, [enabled]);
}

export default function PartsRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [dutyData, setDutyData] = useState(null);

  const hasAccess = user && (user.parts_access || user.role === "admin");

  const load = async () => {
    try {
      const { data } = await api.get("/parts/requests");
      setItems(data.items || []);
      setCanManage(!!data.can_manage);
    } catch {
      setItems([]);
    }
  };

  // Live — WebSocket push on any parts change (30s poll + focus as fallback),
  // duty roster every 20s.
  useEffect(() => {
    if (!hasAccess) return undefined;
    let alive = true;
    const loadDuty = () => api.get("/parts/on-duty").then((r) => { if (alive) setDutyData(r.data); }).catch(() => {});
    const refreshAll = () => { load(); loadDuty(); };
    refreshAll();
    let ws = null;
    let retry = null;
    let debounce = null;
    const connect = () => {
      if (!alive) return;
      try {
        ws = new WebSocket(`${API_BASE.replace(/^http/, "ws")}/ws/parts`);
      } catch {
        retry = setTimeout(connect, 5000);
        return;
      }
      ws.onmessage = () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => { load(); }, 250);
      };
      ws.onclose = () => { if (alive) retry = setTimeout(connect, 3000); };
    };
    connect();
    const iv1 = setInterval(load, 30000);
    const iv2 = setInterval(loadDuty, 20000);
    window.addEventListener("focus", refreshAll);
    return () => {
      alive = false;
      clearTimeout(retry);
      clearTimeout(debounce);
      clearInterval(iv1);
      clearInterval(iv2);
      window.removeEventListener("focus", refreshAll);
      try { ws && ws.close(); } catch {}
    };
  }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDutyStaff = (dutyData?.staff || []).filter((s) => s.on_duty);
  const noOneOnDuty = dutyData !== null && onDutyStaff.length === 0;

  const openCount = (items || []).filter((i) => i.status === "open").length;
  useOpenRequestAlarm(hasAccess && canManage && openCount > 0);

  const changeStatus = async (item, status) => {
    if (status === item.status) return;
    try {
      await api.patch(`/parts/requests/${item.id}/status`, { status });
      toast.success(`${item.part_name} → ${STATUS_META[status].label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="parts-no-access">
        <Package className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold tracking-tight mt-3">Parts Requests</h1>
        <p className="text-sm text-gray-500 mt-2">You don't have access to this page. Ask an admin to grant Parts access in Admin Settings.</p>
      </div>
    );
  }

  const active = (items || []).filter((i) => i.status !== "complete");
  const completed = (items || []).filter((i) => i.status === "complete");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="parts-requests-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Parts Requests</h1>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Stores · MST</span>
        </div>
        <Button
          size="sm"
          className="bg-summit-primary hover:bg-summit-primary-soft text-white"
          onClick={() => setShowForm((v) => !v)}
          data-testid="parts-form-toggle"
        >
          <Plus className="w-4 h-4 mr-1" /> Request a part
        </Button>
      </div>

      {canManage && openCount > 0 && (
        <div className="mt-4 rounded-lg border-2 border-rose-500 bg-rose-50 px-4 py-3 flex items-center gap-3 animate-pulse" data-testid="parts-open-alarm">
          <BellRing className="w-5 h-5 text-rose-600 shrink-0" />
          <div className="text-sm font-medium text-rose-800">
            {openCount} open part request{openCount > 1 ? "s" : ""} waiting — set to <b>In progress</b> to silence this alarm.
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-5">
          {showForm && <RequestForm onCreated={load} onClose={() => setShowForm(false)} noOneOnDuty={noOneOnDuty} />}

          <div>
            <h2 className="font-display text-lg font-medium tracking-tight">Active requests</h2>
            <div className="mt-3 space-y-2" data-testid="parts-active-list">
              {items === null ? (
                <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
              ) : active.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center border border-dashed border-summit-hairline rounded-lg" data-testid="parts-active-empty">
                  No active requests.
                </p>
              ) : (
                active.map((i) => <RequestCard key={i.id} item={i} canManage={canManage} onStatusChange={changeStatus} />)
              )}
            </div>
          </div>

          <div>
            <button
              type="button"
              className="flex items-center gap-1.5 font-display text-lg font-medium tracking-tight text-gray-700 hover:text-gray-900"
              onClick={() => setShowCompleted((v) => !v)}
              data-testid="parts-completed-toggle"
            >
              {showCompleted ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Completed · {completed.length}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-1 ml-5.5">
              Completed requests are removed automatically after 12 hours
            </p>
            {showCompleted && (
              <div className="mt-3 space-y-2" data-testid="parts-completed-list">
                {completed.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center border border-dashed border-summit-hairline rounded-lg" data-testid="parts-completed-empty">
                    Nothing completed in the last 12 hours.
                  </p>
                ) : (
                  completed.map((i) => <RequestCard key={i.id} item={i} canManage={canManage} onStatusChange={changeStatus} />)
                )}
              </div>
            )}
          </div>
        </div>

        <OnDutyPanel staff={onDutyStaff} loaded={dutyData !== null} />
      </div>
    </div>
  );
}
